import {
  Component, OnInit, Input, ViewEncapsulation, ElementRef, HostBinding, TemplateRef,
  ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges, SimpleChange, OnDestroy, ContentChild, ViewChild
} from '@angular/core';
import {DataTable, ColumnModelGenerator, Row} from '../../base';
import {Subscription} from 'rxjs';
import {BodyComponent} from '../body/body.component';
import {PageEvent} from '../../../pagination';
import {HeaderTemplateDirective} from '../../directives/header-template.directive';
import {RowGroupTemplateDirective} from '../../directives/row-group-template.directive';
import { CommonFilterService } from 'src/app/shared/services/common-filter/common-filter.service';

@Component({
  selector: 'app-datatable, app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: [
    './data-table.component.css',
    '../../../styles/checkbox.css',
    '../../../styles/radio.css',
    '../../../styles/buttons.css',
    '../../../styles/list-menu.css',
    '../../../styles/input.css',
    '../../../styles/spinners.css',
    '../../../styles/icons.css',
    '../../../resizable/resizable.css',
  ],
  //https://vibhas1892.medium.com/what-is-viewencapsulation-in-angular-58d31901901c
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent implements OnChanges, OnDestroy {

  @Input() table: DataTable;
  @Input() rsltType: string;

  @ContentChild(HeaderTemplateDirective, {static: true}) headerTemplate: HeaderTemplateDirective;
  @ContentChild(RowGroupTemplateDirective, {static: true}) rowGroupTemplate: RowGroupTemplateDirective;

  @ViewChild('resizeHelper', {static: true}) resizeHelper: ElementRef;
  @ViewChild('footer', {static: true}) footerViewChild: ElementRef;
  @ViewChild(BodyComponent, {static: false}) body: BodyComponent;
  @ViewChild('rowCheckboxTemplate', {static: true}) rowCheckboxTemplate: TemplateRef<any>;
  @ViewChild('headerCheckboxTemplate', {static: true}) headerCheckboxTemplate: TemplateRef<any>;
  //@ViewChild('onlyDelActionTemplate', { static: true }) onlyDelActionTemplate: TemplateRef<any>;

  @HostBinding('class.datatable') cssClass = true;
  @HostBinding('attr.role') role = 'grid';

  @HostBinding('class.fixed-header')
  get isFixedHeader(): boolean {
    return (this.table.dimensions.headerRowHeight) ? true : false;
  }

  loading: boolean;
  scrollDisplay: boolean;
  private subscriptions: Subscription[] = [];

  constructor(private element: ElementRef, private cd: ChangeDetectorRef) { }//, private commonFilterService: CommonFilterService

  ngOnChanges(changes: SimpleChanges) {
    if (this.table) {
      const actionColumn = this.table.columns.find(x => x.name === ColumnModelGenerator.checkboxColumn.name);
      if (actionColumn) {
        actionColumn.cellTemplate = this.rowCheckboxTemplate;
        actionColumn.headerCellTemplate = this.headerCheckboxTemplate;
      }
      //const delColumn = this.table.columns.find(x => x.name === ColumnModelGenerator.delColumn.name);
      //if (delColumn) {
      //  delColumn.cellTemplate = this.onlyDelActionTemplate;
      //}

      const subFilter = this.table.events.filterSource$.subscribe(() => {
        this.onFilter();
      });
      const subSort = this.table.events.sortSource$.subscribe(() => {
        this.onSort();
      });
      const subColumnResizeBegin = this.table.events.resizeBeginSource$.subscribe(() => {
        this.onColumnResizeBegin();
      });
      const subColumnResize = this.table.events.resizeSource$.subscribe((event) => {
        this.onColumnResize(event);
      });
      const subColumnResizeEnd = this.table.events.resizeEndSource$.subscribe(() => {
        this.onColumnResizeEnd();
      });
      const subScroll = this.table.events.scrollSource$.subscribe(() => {
        requestAnimationFrame(() => this.cd.detectChanges());
      });
      const subLoading = this.table.events.loadingSource$.subscribe((event) => {
        this.loading = event;
        if (this.table.rsltType == 'VqtData' && this.table.selectedColumns.length > 0)
          this.vqtColumnTogglerSave();
        //this.commonFilterService.SetItemsRest(this.table.rowsRest);

        //this.table.rowsRest = [];
        ////console.log(this.table.rowsRest)
        this.cd.markForCheck();
        // for server-side virtual scroll
        if (this.table.settings.virtualScroll) {
          requestAnimationFrame(() => this.cd.detectChanges());
        }
      });
      this.subscriptions.push(subFilter);
      this.subscriptions.push(subSort);
      this.subscriptions.push(subColumnResizeBegin);
      this.subscriptions.push(subColumnResize);
      this.subscriptions.push(subColumnResizeEnd);
      this.subscriptions.push(subScroll);
      this.subscriptions.push(subLoading);
    }
  }
  
  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  //Get records of the current page selected
  onPageChanged(event: PageEvent): void {
    this.table.pager.current = event.currentPage;
    this.table.pager.perPage = event.perPage;
    this.table.pager.perPageOrCurrPageChanged = event.perPageOrCurrPageChanged;
    this.table.events.onPage();
    if (this.table.settings.virtualScroll) {
      this.body.scroller.setPageOffsetY(event.currentPage);
    } else {
      if (this.table.clientSide) {
        //console.log('paging');
        this.table.loadLocalRows();
      }
    }
    this.table.selection.clearSelection();
  }

  onFilter() {
    this.table.pager.current = 1;
    if (this.table.clientSide) {
      this.table.loadLocalRows();
    }
    this.table.selection.clearSelection();
    //console.log('onFilter')
  }

  onSort() {
    if (this.table.clientSide) {
      this.table.loadLocalRows();
    }
    this.table.selection.clearSelection();
  }

  onColumnResizeBegin() {
    this.element.nativeElement.classList.add('datatable-unselectable');
    const height = this.element.nativeElement.offsetHeight - this.footerViewChild.nativeElement.offsetHeight;
    this.resizeHelper.nativeElement.style.height = height + 'px';
  }

  onColumnResize(event) {
    const rect = this.element.nativeElement.getBoundingClientRect();
    const containerLeft = rect.left + document.body.scrollLeft;
    this.resizeHelper.nativeElement.style.left = (event.pageX - containerLeft + this.element.nativeElement.scrollLeft) + 'px';
    this.resizeHelper.nativeElement.style.display = 'block';
  }

  onColumnResizeEnd() {
    this.resizeHelper.nativeElement.style.display = 'none';
    this.element.nativeElement.classList.remove('datatable-unselectable');
    this.table.dimensions.recalcColumns();
  }

  get headerVisible(): boolean {
    return this.table.dimensions.headerRowHeight === 0 ? false : true;
  }

  onCheckboxClick(row: Row) {
    this.table.selection.toggle(row.$$index);
    this.table.events.onCheckbox(row);
  }
  //onRowDelClick(event: Event, row: Row) {
  //  confirm('Delete ?') ? this.dataManager.delete(row) : null;
  //}


  vqtColumnTogglerSave() {
    this.table.columns.forEach(x => { x.tableHidden = true; x.index = 99; });
    this.table.selectedColumns.forEach((el, i) => {
      const index = this.table.columns.findIndex(x => x.name === el.id);
      this.table.columns[index].tableHidden = false;
      this.table.columns[index].index = i;
    });
    this.table.columns.sort((a, b) => (a.index > b.index) ? 1 : (a.index < b.index) ? -1 : 0);
    this.table.initColumns();
    this.table.events.onRowsChanged();
    this.table.events.onResizeEnd();

    this.cd.markForCheck();
  }
}
